import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { PdfDocumentProxy } from '../types';
import { loadPdfDocument } from '../services/pdfDocumentService';
import { loadPdfJs } from '../services/pdfjsLoader';

const QUERY_KEYS = {
  runtime: ['pdf-editor', 'runtime'] as const,
  document: (fileUrl: string) => ['pdf-editor', 'document', fileUrl] as const,
};

export const usePdfRuntimeQuery = () =>
  useQuery({
    queryKey: QUERY_KEYS.runtime,
    queryFn: loadPdfJs,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  });

/**
 * Subscribes to a parsed PDF document, deduplicated across the whole
 * app via React Query. The first subscriber for a given fileUrl parses
 * the PDF; later subscribers reuse the same PdfDocumentProxy instance.
 *
 * Memory hygiene (matters a lot on iOS Safari / mobile Chrome on iOS,
 * which both run on WKWebView with a tight per-process memory budget):
 *
 * - `gcTime: 0` means the cache entry is removed the instant subscriber
 *   count drops to zero, instead of being pinned in cache for the
 *   default 5 minutes. Without this, switching layouts retains every
 *   PDF you've ever opened until that timer expires — which is exactly
 *   the "works on first load, crashes on the second layout switch"
 *   pattern.
 *
 * - When the entry is removed, we explicitly call `pdfDoc.destroy()`.
 *   PDF.js documents own a worker port + parsed page cache + font
 *   cache that JS garbage collection can NOT release on its own. A
 *   24×36 in architectural drawing is typically 30–80 MB; leaking two
 *   or three of them blows past the WKWebView limit and the page
 *   "force refreshes and crashes".
 */
export const usePdfDocumentQuery = (fileUrl: string | null) => {
  const queryClient = useQueryClient();

  // Install the cache subscriber once per QueryClient instance. We tag
  // the client so subsequent hooks don't double-subscribe.
  useEffect(() => {
    const cache = queryClient.getQueryCache();
    const tagged = cache as unknown as { __pdfDestroyHookInstalled?: boolean };
    if (tagged.__pdfDestroyHookInstalled) return;
    tagged.__pdfDestroyHookInstalled = true;

    cache.subscribe((event) => {
      if (event.type !== 'removed') return;
      const key = event.query.queryKey;
      if (
        !Array.isArray(key) ||
        key[0] !== 'pdf-editor' ||
        key[1] !== 'document'
      ) {
        return;
      }
      const data = event.query.state.data as PdfDocumentProxy | undefined;
      if (data && typeof data.destroy === 'function') {
        // destroy() returns a Promise but we don't need to await it;
        // the worker port shutdown happens asynchronously and that's fine.
        try {
          void data.destroy();
        } catch {
          // Some PDF.js builds throw if destroy() is called twice.
          // Swallow — the worker cleanup still happens.
        }
      }
    });
  }, [queryClient]);

  return useQuery<PdfDocumentProxy, Error>({
    queryKey: QUERY_KEYS.document(fileUrl ?? ''),
    enabled: Boolean(fileUrl),
    queryFn: async () => {
      if (!fileUrl) throw new Error('Missing PDF file URL');
      return loadPdfDocument(fileUrl);
    },
    staleTime: 60_000,
    // Drop the entry as soon as the last subscriber goes away — see
    // the comment above for why this is critical.
    gcTime: 0,
  });
};
